import React, { useState } from 'react';
import { X, Calendar, Clock, Music, Star, MapPin } from 'lucide-react';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}

export default function EventDetailModal({ event, onClose, onBook }) {
  if (!event) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="event-detail-modal"
    >
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden relative animate-fade-in"
        style={{ background: '#0d1b2a', border: '1px solid rgba(201,168,76,0.3)', boxShadow: '0 0 80px rgba(201,168,76,0.15)', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="close-event-modal"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(0,0,0,0.6)', color: 'white' }}
        >
          <X size={20} />
        </button>

        {/* Image */}
        <div style={{ height: '280px', overflow: 'hidden', position: 'relative', background: '#080810' }}>
          <img
            src={event.image_url || "/api/uploads/logo_fallback.png"}
            alt={event.title}
            style={event.image_url ? { width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' } : { width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', padding: '2rem' }}
            onError={e => { e.target.style.objectFit='contain'; e.target.style.padding='2rem'; e.target.src="/api/uploads/logo_fallback.png"; }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #0d1b2a 0%, transparent 60%)' }} />
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Special guest badge */}
          {event.special_guest && (
            <div
              className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full font-manrope font-700 text-sm"
              style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.5)', color: 'var(--color-gold)' }}
            >
              <Star size={14} fill="currentColor" /> Special Guest: {event.special_guest}
            </div>
          )}

          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
            {event.title}
          </h2>

          {/* Details row */}
          <div className="flex flex-wrap gap-5 mb-6">
            <div className="flex items-center gap-2 text-gold font-manrope text-lg">
              <Calendar size={20} />
              <span className="capitalize">{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-2 text-aqua font-manrope text-lg">
              <Clock size={20} />
              <span>Ore {event.time}</span>
            </div>
            {event.dj && (
              <div className="flex items-center gap-2 text-gray-300 font-manrope text-lg">
                <Music size={20} className="text-gold" />
                <span>{event.dj}</span>
              </div>
            )}
          </div>

          {/* Genre badge */}
          {event.music_genre && (
            <span
              className="inline-block px-4 py-1.5 rounded-full text-sm font-manrope font-600 mb-5"
              style={{ background: 'rgba(79,195,212,0.1)', border: '1px solid rgba(79,195,212,0.3)', color: 'var(--color-aqua)' }}
            >
              {event.music_genre}
            </span>
          )}

          {/* Description */}
          <p className="text-gray-300 text-lg leading-relaxed mb-6">{event.description}</p>

          {/* Location */}
          <div className="flex items-start gap-3 mb-8 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <MapPin size={20} className="text-gold mt-0.5 shrink-0" />
            <div>
              <p className="text-white font-600 font-manrope">La Peschiera Club</p>
              <p className="text-gray-400 font-manrope text-sm">Via Q. Sella 65, Valdengo (BI) 13900</p>
              <p className="text-gray-500 font-manrope text-sm mt-0.5">Ampio parcheggio gratuito esterno</p>
            </div>
          </div>

          {/* Payment notice */}
          <div
            className="flex items-center gap-3 p-4 rounded-xl mb-6"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}
          >
            <span className="text-2xl">💵</span>
            <p className="text-gold font-manrope font-700 text-base">Il pagamento avviene esclusivamente in cassa all'ingresso</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={onClose} className="btn-secondary flex-1 justify-center">Chiudi</button>
            <button
              onClick={() => { onBook(event); onClose(); }}
              data-testid="modal-book-btn"
              className="btn-primary flex-1 justify-center"
            >
              Prenota per questa serata
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
